import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Modal,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Trans, useTranslation } from 'react-i18next';
import Register from '../components/Register';
import PersonnelRegister from '../components/PersonnelRegister';

const RoleSelectionModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: 500,
  padding: 0,
  outline: 'none',
  borderRadius: 16,
  overflow: 'hidden',
}));

const RegisterEmbedModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: 520,
  maxHeight: '90vh',
  padding: 0,
  outline: 'none',
  borderRadius: 16,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const RoleButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4, 2),
  margin: theme.spacing(2),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: 16,
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  transition: 'all 0.3s ease',
  height: '220px',
  width: '100%',
  '&:hover': {
    backgroundColor: 'rgba(13, 71, 161, 0.04)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  },
}));

const RoleIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '60px',
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const RoleTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.main,
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const RoleSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: '1.2',
}));

type EmbedKind = 'admin' | 'employee' | null;

interface PublicRegistrationFlowContextValue {
  openRoleSelection: () => void;
}

const PublicRegistrationFlowContext =
  createContext<PublicRegistrationFlowContextValue | null>(null);

export const usePublicRegistrationFlow = (): PublicRegistrationFlowContextValue => {
  const ctx = useContext(PublicRegistrationFlowContext);
  if (!ctx) {
    return { openRoleSelection: () => {} };
  }
  return ctx;
};

export const PublicRegistrationFlowProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [embedKind, setEmbedKind] = useState<EmbedKind>(null);

  const openRoleSelection = useCallback(() => {
    setEmbedKind(null);
    setRoleModalOpen(true);
  }, []);

  const closeRoleModal = useCallback(() => {
    setRoleModalOpen(false);
  }, []);

  const selectAdmin = useCallback(() => {
    setRoleModalOpen(false);
    setEmbedKind('admin');
  }, []);

  const selectPersonnel = useCallback(() => {
    setRoleModalOpen(false);
    setEmbedKind('employee');
  }, []);

  const closeEmbedOnly = useCallback(() => {
    setEmbedKind(null);
  }, []);

  const backFromEmbedToRole = useCallback(() => {
    setEmbedKind(null);
    setRoleModalOpen(true);
  }, []);

  const value = useMemo(
    () => ({ openRoleSelection }),
    [openRoleSelection]
  );

  return (
    <PublicRegistrationFlowContext.Provider value={value}>
      {children}

      <Modal
        open={roleModalOpen}
        onClose={closeRoleModal}
        aria-labelledby="public-role-selection-title"
      >
        <RoleSelectionModalContainer>
          <Card elevation={5} sx={{ width: '100%', overflow: 'hidden' }}>
            <Box
              sx={{
                py: 3,
                px: 3,
                textAlign: 'center',
                background: 'linear-gradient(to right, #0D47A1, #1976D2)',
                color: 'white',
              }}
            >
              <Typography
                id="public-role-selection-title"
                variant="h5"
                component="h2"
                fontWeight="bold"
              >
                {t('loginPage.roleSelection.title')}
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="body1"
                align="center"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                {t('loginPage.roleSelection.instruction')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                }}
              >
                <RoleButton onClick={selectAdmin}>
                  <RoleIcon>
                    <AdminPanelSettingsIcon fontSize="large" sx={{ fontSize: 48 }} />
                  </RoleIcon>
                  <RoleTitle>{t('loginPage.roleSelection.adminRole')}</RoleTitle>
                  <RoleSubtitle
                    sx={{
                      height: 'auto',
                      lineHeight: 1.2,
                      paddingTop: 0,
                      marginTop: 0,
                    }}
                  >
                    <Trans i18nKey="loginPage.roleSelection.adminDescription" />
                  </RoleSubtitle>
                </RoleButton>

                <RoleButton onClick={selectPersonnel}>
                  <RoleIcon>
                    <PersonOutlineIcon fontSize="large" sx={{ fontSize: 48 }} />
                  </RoleIcon>
                  <RoleTitle>{t('loginPage.roleSelection.personnelRole')}</RoleTitle>
                  <RoleSubtitle>
                    {t('loginPage.roleSelection.personnelDescription')}
                  </RoleSubtitle>
                </RoleButton>
              </Box>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button onClick={closeRoleModal} sx={{ color: 'text.secondary' }}>
                  {t('loginPage.roleSelection.cancelButton')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </RoleSelectionModalContainer>
      </Modal>

      <Modal
        open={embedKind !== null}
        onClose={closeEmbedOnly}
        aria-labelledby="public-register-embed-title"
      >
        <RegisterEmbedModalContainer>
          <Card
            elevation={5}
            sx={{
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <CardContent
              id="public-register-embed-title"
              sx={{
                p: { xs: 2, sm: 2.5 },
                overflow: 'auto',
                flex: 1,
                minHeight: 0,
              }}
            >
              {embedKind === 'admin' && (
                <Register
                  embedded
                  onRequestClose={closeEmbedOnly}
                  onEmbeddedBack={backFromEmbedToRole}
                />
              )}
              {embedKind === 'employee' && (
                <PersonnelRegister
                  embedded
                  onRequestClose={closeEmbedOnly}
                  onEmbeddedBack={backFromEmbedToRole}
                />
              )}
            </CardContent>
          </Card>
        </RegisterEmbedModalContainer>
      </Modal>
    </PublicRegistrationFlowContext.Provider>
  );
};
